/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ProcessInstanceRepresentation, ResultListDataRepresentationDecisionTaskRepresentation, ResultListDataRepresentationProcessContentRepresentation, ResultListDataRepresentationProcessInstanceRepresentation } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class ProcessInstancesApi extends BaseApi {
    activateProcessInstance(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.put({
            path: '/api/enterprise/process-instances/{processInstanceId}/activate',
            pathParams,
            returnType: ProcessInstanceRepresentation
        });
    }
    createIdentityLink(processInstanceId, identityLinkRepresentation) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        throwIfNotDefined(identityLinkRepresentation, 'identityLinkRepresentation');
        const pathParams = {
            processInstanceId
        };
        return this.post({
            path: '/api/enterprise/process-instances/{processInstanceId}/identitylinks',
            pathParams,
            bodyParam: identityLinkRepresentation
        });
    }
    deleteIdentityLink(processInstanceId, family, identityId, type) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        throwIfNotDefined(family, 'family');
        throwIfNotDefined(identityId, 'identityId');
        throwIfNotDefined(type, 'type');
        const pathParams = {
            processInstanceId,
            family,
            identityId,
            type
        };
        return this.delete({
            path: '/api/enterprise/process-instances/{processInstanceId}/identitylinks/{family}/{identityId}/{type}',
            pathParams
        });
    }
    deleteProcessInstance(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.delete({
            path: '/api/enterprise/process-instances/{processInstanceId}',
            pathParams
        });
    }
    filterProcessInstances(filterRequest) {
        throwIfNotDefined(filterRequest, 'filterRequest');
        return this.post({
            path: '/api/enterprise/process-instances/filter',
            bodyParam: filterRequest,
            returnType: ResultListDataRepresentationProcessInstanceRepresentation
        });
    }
    getHistoricProcessInstanceDecisionTasks(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/decision-tasks',
            pathParams,
            returnType: ResultListDataRepresentationDecisionTaskRepresentation
        });
    }
    getHistoricProcessInstanceVariables(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/historic-variables',
            pathParams
        });
    }
    getHistoricProcessInstances(queryRequest) {
        throwIfNotDefined(queryRequest, 'queryRequest');
        return this.post({
            path: '/api/enterprise/historic-process-instances/query',
            bodyParam: queryRequest,
            returnType: ResultListDataRepresentationProcessInstanceRepresentation
        });
    }
    getIdentityLinkType(processInstanceId, family, identityId, type) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        throwIfNotDefined(family, 'family');
        throwIfNotDefined(identityId, 'identityId');
        throwIfNotDefined(type, 'type');
        const pathParams = {
            processInstanceId,
            family,
            identityId,
            type
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/identitylinks/{family}/{identityId}/{type}',
            pathParams
        });
    }
    getIdentityLinksForFamily(processInstanceId, family) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        throwIfNotDefined(family, 'family');
        const pathParams = {
            processInstanceId,
            family
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/identitylinks/{family}',
            pathParams
        });
    }
    getIdentityLinks(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/identitylinks',
            pathParams
        });
    }
    getProcessInstanceContent(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/field-content',
            pathParams,
            returnType: ResultListDataRepresentationProcessContentRepresentation
        });
    }
    getProcessInstanceDiagram(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        const accepts = ['image/png'];
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/diagram',
            pathParams,
            accepts
        });
    }
    getProcessInstanceStartForm(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/start-form',
            pathParams
        });
    }
    getProcessInstance(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}',
            pathParams,
            returnType: ProcessInstanceRepresentation
        });
    }
    getProcessInstances(processInstancesQuery) {
        throwIfNotDefined(processInstancesQuery, 'processInstancesQuery');
        return this.post({
            path: '/api/enterprise/process-instances/query',
            bodyParam: processInstancesQuery,
            returnType: ResultListDataRepresentationProcessInstanceRepresentation
        });
    }
    getTaskAuditLog(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/audit-log',
            pathParams
        });
    }
    getProcessAuditPdf(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        const responseType = 'blob';
        return this.get({
            path: '/app/rest/process-instances/{processInstanceId}/audit',
            pathParams,
            responseType
        });
    }
    startNewProcessInstance(startRequest) {
        throwIfNotDefined(startRequest, 'startRequest');
        return this.post({
            path: '/api/enterprise/process-instances',
            bodyParam: startRequest,
            returnType: ProcessInstanceRepresentation
        });
    }
    suspendProcessInstance(processInstanceId) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        const pathParams = {
            processInstanceId
        };
        return this.put({
            path: '/api/enterprise/process-instances/{processInstanceId}/suspend',
            pathParams,
            returnType: ProcessInstanceRepresentation
        });
    }
}
